// =============================================================================
// ChatList/commercialSignals.ts — lógica pura dos 3 sinais comerciais do
// ChatListItem (F38-S05, doc planejamento-livechat-contexto-comercial.md §2.1).
//
// Os campos do DTO (nextAppointmentAt/activeCampaign/contactRestricted, vindos
// de F38-S02) são OPCIONAIS:
//   - `undefined` = flag `livechat.commercial_context.enabled` off para o ator
//     (ou cache antigo do front) → SEM sinal, nunca quebra o parse.
//   - `null` (appointment/campaign) ou `false` (restricted) = flag on mas sem
//     sinal para este lead → também SEM sinal.
// Em ambos os casos as funções abaixo retornam `null`/`false` — o item da
// lista renderiza exatamente como hoje, sem nenhum nó extra.
// =============================================================================

import { formatOrgDayMonth, formatOrgTime, isTodayInOrgTz } from './dateTimeOrgTz';

export interface AppointmentSignal {
  /** `true` quando o retorno cai hoje no fuso da organização (âmbar). */
  readonly isToday: boolean;
  /** Texto acessível completo, usado em `title`/`aria-label`. */
  readonly label: string;
}

/**
 * Deriva o sinal de retorno agendado a partir de `nextAppointmentAt`.
 *
 * `undefined`/`null`/ISO inválido → `null` (sem sinal).
 * `reference` é injetável para testes determinísticos.
 */
export function computeAppointmentSignal(
  nextAppointmentAt: string | null | undefined,
  reference: Date = new Date(),
): AppointmentSignal | null {
  if (nextAppointmentAt === null || nextAppointmentAt === undefined) return null;
  const date = new Date(nextAppointmentAt);
  if (Number.isNaN(date.getTime())) return null;

  const isToday = isTodayInOrgTz(nextAppointmentAt, reference);
  const time = formatOrgTime(nextAppointmentAt);
  const label = isToday
    ? `Retorno agendado para hoje às ${time}`
    : `Retorno agendado para ${formatOrgDayMonth(nextAppointmentAt)} às ${time}`;

  return { isToday, label };
}

/**
 * `true` somente quando há restrição de contato EFETIVA a sinalizar.
 * `undefined` (flag off) e `false` (sem restrição) devolvem `false` — sem sinal.
 */
export function hasRestrictionSignal(contactRestricted: boolean | undefined): boolean {
  return contactRestricted === true;
}

/**
 * `true` quando há campanha ativa a sinalizar. `undefined` (flag off) e
 * `null` (sem campanha ativa) devolvem `false` — sem sinal.
 */
export function hasCampaignSignal(
  activeCampaign: { readonly name: string } | null | undefined,
): activeCampaign is { readonly name: string } {
  return activeCampaign !== null && activeCampaign !== undefined;
}
